import React, { useState } from 'react';
import { Menu, X, ArrowRight, Target, Users, TrendingUp, Layers, CheckCircle2, ChevronRight, Activity, Zap } from 'lucide-react';

/* --- COMPONENTS --- */

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const brandLogo = `${process.env.PUBLIC_URL}/NuvioLogo.png?v=20260314c`;

  // Link data to keep mobile and desktop in sync
  const navLinks = [
    {
      name: 'About',
      href: '/home/about',
      children: [{ name: 'Why Nuvio?', href: '/home/why-nuvio' }],
    },
    { name: 'For Coaches', href: '/home/programs' },
    { name: 'For Athletes', href: '/home/athletes' },
    { name: 'Features', href: '/home/features' },
    { name: 'Pricing', href: '/home/pricing' },
    { name: 'Case Studies', href: '/home/case-studies' },
  ];

  return (
    <nav className="fixed top-0 left-0 w-full bg-white/95 backdrop-blur-md border-b border-slate-100 z-50 transition-all">
      <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
        {/* Brand */}
        <a href="/" className="flex items-center gap-2 cursor-pointer group">
          <img
            src={brandLogo}
            alt="Nuvio"
            className="w-8 h-8 rounded-lg object-cover shadow-sm transition-transform group-hover:scale-105"
          />
          <span className="text-xl font-bold tracking-tight text-slate-900">
            Nuvio<span className="text-blue-600">Sport</span>
          </span>
        </a>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.name} className="relative group">
                <a
                  href={link.href}
                  className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors"
                >
                  {link.name}
                </a>
                <div className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all">
                  <div className="bg-white border border-slate-100 shadow-xl rounded-xl p-3 min-w-[180px]">
                    {link.children.map((child) => (
                      <a
                        key={child.name}
                        href={child.href}
                        className="block px-3 py-2 text-sm font-semibold text-slate-700 rounded-lg hover:bg-slate-50 hover:text-blue-600"
                      >
                        {child.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors"
              >
                {link.name}
              </a>
            )
          )}
          <div className="flex gap-4 items-center ml-4">
            <a href="/login" className="text-sm font-bold text-slate-700 hover:text-blue-600 px-4 py-2 transition-colors">
              Log In
            </a>
            <a href="/create-account" className="px-6 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20">
              Create Account
            </a>
          </div>
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden text-slate-700 p-2 hover:bg-slate-50 rounded-lg transition-colors"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      <div className={`
        lg:hidden absolute top-20 left-0 w-full bg-white border-b border-slate-100 transition-all duration-300 ease-in-out overflow-hidden
        ${isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}
      `}>
        <div className="px-6 py-8 flex flex-col gap-6 shadow-xl">
          {navLinks.map((link) => (
            <div key={link.name} className="flex flex-col gap-2">
              <a
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="text-lg font-bold text-slate-900 hover:text-blue-600"
              >
                {link.name}
              </a>
              {link.children && (
                <div className="ml-4 flex flex-col gap-2">
                  {link.children.map((child) => (
                    <a
                      key={child.name}
                      href={child.href}
                      onClick={() => setIsOpen(false)}
                      className="text-sm font-semibold text-slate-600 hover:text-blue-600"
                    >
                      {child.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          <hr className="border-slate-100" />
          <div className="flex flex-col gap-4">
            <a
              href="/login"
              onClick={() => setIsOpen(false)}
              className="w-full py-4 text-center font-bold text-slate-700 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
            >
              Log In
            </a>
            <a
              href="/create-account"
              onClick={() => setIsOpen(false)}
              className="w-full py-4 text-center font-bold text-white bg-blue-600 rounded-xl shadow-lg shadow-blue-600/20 hover:bg-blue-700 transition-all"
            >
              Create Account
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

const Hero = () => {
  return (
    <section className="bg-white pt-36 pb-20 px-6">
      <div className="max-w-4xl mx-auto text-center">
        {/* Authority Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest mb-8">
          <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
          Developing Consistent Success
        </div>

        {/* H1: The Core Value Prop for Admins */}
        <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 tracking-tight mb-8 leading-[1.1]">
          The Player Development Platform for <br />
          <span className="text-blue-600">Elite Sports Programs.</span>
        </h1>

        {/* Subheadline: The "30 Minute" Hook + Pipeline Promise */}
        <p className="text-xl text-slate-500 max-w-2xl mx-auto mb-10 leading-relaxed font-medium">
          Designed to help your program win more games and get your players ready for the next level.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a href="/create-account" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-blue-600 text-white font-bold text-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-xl shadow-blue-600/20">
            Cement Your System <ArrowRight size={20} />
          </a>
          <a href="https://calendar.app.google/919vQjWVJnBKRPgA8" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white text-slate-700 border-2 border-slate-100 font-bold text-lg hover:bg-slate-50 hover:border-slate-200 transition-all">
            Schedule a Demo
          </a>
        </div>
      </div>
    </section>
  );
};

/* --- The "Split" Section: Admins vs Athletes --- */
const Personas = () => {
  return (
    <section className="py-16 px-6 bg-white">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Administrator / Program Director */}
        <div className="p-10 rounded-3xl bg-slate-50 border border-slate-200 hover:shadow-xl hover:shadow-slate-200/40 transition-all duration-300">
          <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-8 shadow-sm text-blue-600 border border-slate-100">
            <Users size={32} />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-3">For Coaches & Directors</h3>
          <p className="text-slate-500 mb-8 text-lg">Fragmentation is the enemy of culture. Regain control of your program's development pipeline.</p>

          <ul className="space-y-4 mb-10">
            <ul className="space-y-4 mb-10">
              <li className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-blue-600 mt-1 shrink-0" />
                <span className="text-slate-700 font-medium">
                  <strong>Set the Standard:</strong> Choose the skills that matter most to you and your system.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-blue-600 mt-1 shrink-0" />
                <span className="text-slate-700 font-medium">
                  <strong>Build Alignment:</strong> Every player trains toward your system.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-blue-600 mt-1 shrink-0" />
                <span className="text-slate-700 font-medium">
                  <strong>See Real Growth:</strong> Track progress before the season starts.
                </span>
              </li>
            </ul>

          </ul>
          <a href="/create-account" className="inline-flex items-center gap-2 text-blue-700 font-extrabold uppercase tracking-wider text-xs border-b-2 border-blue-200 hover:border-blue-600 pb-1 transition-all">
            Develop Your Program <ArrowRight size={14} />
          </a>
        </div>

        {/* Athlete / Parent */}
        <div className="p-10 rounded-3xl bg-slate-900 text-white border border-slate-800 shadow-2xl shadow-slate-900/20">
          <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-8 text-blue-400 backdrop-blur-sm">
            <Target size={32} />
          </div>
          <h3 className="text-2xl font-bold mb-3">For Athletes & Parents</h3>
          <p className="text-slate-400 mb-8 text-lg">Stop guessing what the coach wants. Get the exact roadmap to earning your spot.</p>

          <ul className="space-y-4 mb-10">
            <ul className="space-y-4 mb-10">
              <li className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-blue-500 mt-1 shrink-0" />
                <span className="text-slate-300 font-medium">
                  <strong>Clear Goals:</strong> Know exactly what your coach expects.
                </span>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-blue-500 mt-1 shrink-0" />
                <span className="text-slate-300 font-medium">
                  <strong>Daily Plan:</strong> Train with purpose and intentionality every time.
                </span>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-blue-500 mt-1 shrink-0" />
                <span className="text-slate-300 font-medium">
                  <strong>Real Progress:</strong> Every workout builds on each other. Prevent stagnation and achieve your goals.
                </span>
              </li>
            </ul>

          </ul>
          <a href="/create-account" className="inline-flex items-center gap-2 text-blue-400 font-extrabold uppercase tracking-wider text-xs border-b-2 border-blue-400/30 hover:border-blue-400 pb-1 transition-all">
            Start Your Nuvio Journey <ArrowRight size={14} />
          </a>
        </div>

      </div>
    </section>
  )
}

const Features = () => {
  const features = [
    {
      title: "Your Program Blueprint",
      desc: "Turn your coaching ideas into a clear plan every athlete can follow.",
      icon: <Layers className="text-blue-600" size={24} />
    },
    {
      title: "The Accountability Engine",
      desc: "Our system tracks engagement and progression automatically.",
      icon: <TrendingUp className="text-blue-600" size={24} />
    },
    {
      title: "Fast Setup",
      desc: "We don't just give you a login. We align your entire department in minutes, not months. Focus on coaching, not IT.",
      icon: <Zap className="text-blue-600" size={24} />
    }
  ];

  return (
    <section id="features" className="py-24 px-6 bg-white border-y border-slate-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-extrabold text-slate-900 mb-4">Infrastructure for a Dynasty</h2>
          <p className="text-slate-500">The tools you need to build a consistent winner.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {features.map((item, i) => (
            <div key={i} className="bg-white p-8 rounded-2xl border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform">
                {item.icon}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
              <p className="text-slate-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => (
  <section className="py-24 px-6 bg-slate-50">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="text-3xl font-extrabold text-slate-900 mb-12">The Standard at Winning Programs</h2>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white p-8 rounded-2xl shadow-sm text-left">
          <p className="text-slate-600 italic mb-6">"We didn't just want an app; we wanted a culture of accountability. Nuvio gave us a way to ensure that when our athletes were miles away, they were still practicing <em>our</em> way."</p>
          <div className="font-bold text-slate-900">- Athletic Director, 5A High School</div>
        </div>
        <div className="bg-white p-8 rounded-2xl shadow-sm text-left">
          <p className="text-slate-600 italic mb-6">"The difference in November is night and day. We used to spend the first month reteaching habits. Now, we start installing advanced schemes on day one."</p>
          <div className="font-bold text-slate-900">- Head Coach, National Club Program</div>
        </div>
      </div>
    </div>
  </section>
)

const CtaSection = () => (
  <section className="py-24 px-6 bg-slate-900 text-white w-full">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight">Install Your Standard.</h2>
      <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">We onboard your program, align every athlete, and build your framework.</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
        <a href="/create-account" className="w-full sm:w-auto px-10 py-4 rounded-xl bg-blue-600 text-white font-bold text-lg hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/50">
          Start your Nuvio Journey
        </a>
        <a href="https://calendar.app.google/919vQjWVJnBKRPgA8" className="w-full sm:w-auto px-10 py-4 rounded-xl bg-white/10 text-white font-bold text-lg hover:bg-white/20 transition-all">
          Schedule a Demo
        </a>
      </div>
    </div>
  </section>
)

const Footer = () => (
  <footer className="bg-white text-slate-500 py-16 px-6 border-t border-slate-100 w-full">
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
      <div className="text-center md:text-left">
        <div className="text-slate-900 font-bold text-xl mb-2">NuvioSport</div>
        <p className="text-sm">The operating system for high-performance programs.</p>
      </div>
      <div className="flex gap-8 text-sm font-bold text-slate-700">
        <a href="/home/programs" className="hover:text-blue-600">For Coaches</a>
        <a href="/home/athletes" className="hover:text-blue-600">For Athletes</a>
        <a href="/home/features" className="hover:text-blue-600">Features</a>
        <a href="/home/pricing" className="hover:text-blue-600">Pricing</a>
        <a href="/home/case-studies" className="hover:text-blue-600">Case Studies</a>
        <a href="/home/about" className="hover:text-blue-600">About</a>
        <a href="/home/why-nuvio" className="hover:text-blue-600">Why Nuvio</a>
      </div>
      <div className="text-[12px] font-bold uppercase tracking-widest text-slate-400">
        &copy; 2026 Nuvio Sport Inc.
      </div>
    </div>
  </footer>
);

export default function App() {
  return (
    <div className="min-h-screen w-full bg-white text-slate-900 font-sans antialiased overflow-x-hidden">
      <Navbar />
      <Hero />
      <Personas />
      <Features />
      {/* <Testimonials /> */}
      <CtaSection />
      <Footer />
    </div>
  );
}
